import { HttpClient } from '@angular/common/http';
import { Injectable, computed, signal } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from '../../environments/environment';

/**
 * A single US Treasury yield-curve point, as returned by
 * `GET api/Treasury/GetTreasuryRates`.
 *
 * Keys are lowercased on load (see {@link TreasuryService.getTreasuryRates}) so
 * bindings never depend on the API's exact casing — matching the ReportDataService
 * convention. `asofdate` is an ISO `yyyy-MM-dd` string (see DateOnlyJsonConverter).
 */
export interface TreasuryRate {
  /** Term of the point, in months (1, 2, 3, 4, 6, 12, 24, 36…). */
  months: number | null;
  /** Yield, as a percentage (e.g. 4.55). */
  rates: number | null;
  /** Effective date of the curve, ISO `yyyy-MM-dd`. */
  asofdate: string | null;
}

/** localStorage key holding the date (YYYY-MM-DD) rates were last loaded. */
const STORAGE_KEY = 'treasuryRatesLoadedDate';

/** Today's local calendar date as YYYY-MM-DD (no time component). */
function todayKey(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Loads the latest US Treasury yield curve and tracks whether it's been done.
 *
 * All Actuary & Finance reports price off the latest Treasury rates, so this is
 * a prerequisite step. The API only exposes a "load" action (no status query),
 * so "done for today" is tracked client-side in localStorage, keyed to the
 * current date — a load stays valid for the rest of the day on this browser.
 */
@Injectable({ providedIn: 'root' })
export class TreasuryService {
  /** apiBaseUrl without any trailing slash. */
  private readonly base = environment.apiBaseUrl.replace(/\/+$/, '');
  private readonly loadedDate = signal<string | null>(this.readStored());

  /** The date (YYYY-MM-DD) rates were last loaded on this browser, or null. */
  readonly loadedOn = this.loadedDate.asReadonly();
  /** Whether today's Treasury rates have already been loaded on this browser. */
  readonly isLoadedToday = computed(() => this.loadedDate() === todayKey());

  constructor(private readonly http: HttpClient) {}

  /**
   * Fetches today's Treasury yield curve on the server and stores it.
   * The endpoint returns a plain-text confirmation, hence `responseType: 'text'`.
   */
  loadLatest(): Observable<string> {
    return this.http.post(`${this.base}/api/Treasury/load-latest`, null, {
      responseType: 'text'
    });
  }

  /**
   * Loads all stored US Treasury rates (newest AsOfDate first — see the API's
   * TreasuryRatesRepository). Response keys are lowercased so bindings stay
   * casing-proof, mirroring ReportDataService.
   */
  getTreasuryRates(): Observable<TreasuryRate[]> {
    return this.http
      .get<unknown[]>(`${this.base}/api/Treasury/GetTreasuryRates`)
      .pipe(map((rows) => (rows ?? []).map((row) => normaliseRate(row))));
  }

  /** Records that rates were loaded today (persisted for the rest of the day). */
  markLoadedToday(): void {
    const key = todayKey();
    this.loadedDate.set(key);
    try {
      localStorage.setItem(STORAGE_KEY, key);
    } catch {
      // Ignore storage failures (private mode, quota) — state still lives in the signal.
    }
  }

  private readStored(): string | null {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  }
}

/** Maps a raw API row to a {@link TreasuryRate}, lowercasing every top-level key. */
function normaliseRate(row: unknown): TreasuryRate {
  const out: Record<string, unknown> = {};
  if (row && typeof row === 'object') {
    for (const [key, value] of Object.entries(row as Record<string, unknown>)) {
      out[key.toLowerCase()] = value;
    }
  }
  return {
    months: (out['months'] as number) ?? null,
    rates: (out['rates'] as number) ?? null,
    asofdate: (out['asofdate'] as string) ?? null
  };
}
